import { DEFAULT_SPORT, normalizeSportId, SPORT_IDS } from "../domain/sport/sportTypes";

/**
 * Central sport module registry.
 * - Unknown / missing ids fall back to default (badminton) for legacy sessions.
 * - Explicit registered sports are returned as-is (even if not playable) — never silently
 *   swap pickleball → badminton scoring.
 */
export class SportRegistry {
  constructor(defaultSportId = DEFAULT_SPORT) {
    this.defaultSportId = normalizeSportId(defaultSportId);
    /** @type {Map<string, import("./SportModule").SportModule>} */
    this.modules = new Map();
  }

  /**
   * @param {import("./SportModule").SportModule} mod
   */
  register(mod) {
    if (!mod?.id) {
      throw new Error("SportRegistry.register requires a module with id");
    }
    const id = String(mod.id).trim().toLowerCase();
    this.modules.set(id, { ...mod, id });
    return this;
  }

  /**
   * @param {string | null | undefined} sportId
   * @returns {import("./SportModule").SportModule | null}
   */
  resolve(sportId) {
    const raw = String(sportId || "").trim().toLowerCase();
    if (!raw) {
      return this.modules.get(this.defaultSportId) || null;
    }
    if (this.modules.has(raw)) {
      return this.modules.get(raw) || null;
    }
    // Reserved id in SPORT_IDS but not registered → fail closed (null), don't score as badminton
    if (Object.values(SPORT_IDS).includes(/** @type {any} */ (raw))) {
      return null;
    }
    // Truly unknown legacy ids → default badminton
    return this.modules.get(this.defaultSportId) || null;
  }

  /**
   * @param {string | null | undefined} sportId
   */
  resolveOrDefault(sportId) {
    return this.resolve(sportId) || this.modules.get(this.defaultSportId) || null;
  }

  list() {
    return [...this.modules.values()];
  }

  listPlayable() {
    return this.list().filter((m) => m.isPlayable);
  }
}
